'use client';

import * as React from 'react';
import { cn } from './cn';
import { CONTROL_BASE, CONTROL_TEXT, Field } from './Field';
import { useComposedRefs } from './hooks';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Grow with content instead of scrolling. */
  autoResize?: boolean;
  /** Adds extra leading so Hebrew nikud marks are never clipped. */
  nikud?: boolean;
  /** Shows a `value.length / maxLength` counter. */
  showCount?: boolean;
  containerClassName?: string;
}

/** Multi-line text. `dir="auto"` per box; `nikud` opens up the line height. */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    autoResize,
    nikud,
    showCount,
    className,
    containerClassName,
    dir = 'auto',
    rows = 3,
    id: idProp,
    maxLength,
    value,
    defaultValue,
    onChange,
    required,
    ...props
  },
  ref,
) {
  const innerRef = React.useRef<HTMLTextAreaElement>(null);
  const composed = useComposedRefs<HTMLTextAreaElement>(ref, innerRef);
  const [count, setCount] = React.useState(String(value ?? defaultValue ?? '').length);

  const resize = React.useCallback(() => {
    const el = innerRef.current;
    if (!el || !autoResize) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoResize]);

  React.useEffect(resize, [resize, value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCount(e.target.value.length);
    resize();
    onChange?.(e);
  };

  const render = (ids: { id: string; describedBy?: string; invalid: boolean }) => (
    <div className="relative">
      <textarea
        ref={composed}
        id={idProp ?? ids.id}
        dir={dir}
        rows={rows}
        required={required}
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        aria-invalid={ids.invalid || undefined}
        aria-describedby={ids.describedBy}
        className={cn(
          CONTROL_BASE,
          CONTROL_TEXT,
          'min-h-touch resize-y px-3.5 py-2.5',
          nikud && 'k-nikud',
          autoResize && 'resize-none overflow-hidden',
          showCount && maxLength && 'pb-6',
          className,
        )}
        {...props}
      />
      {showCount && maxLength ? (
        <span className="text-2xs pointer-events-none absolute bottom-2 end-3 tabular-nums text-ink-3" dir="ltr">
          {count}/{maxLength}
        </span>
      ) : null}
    </div>
  );

  if (!label && !hint && !error) {
    return <>{render({ id: idProp ?? '', describedBy: props['aria-describedby'], invalid: false })}</>;
  }
  return (
    <Field label={label} hint={hint} error={error} required={required} className={containerClassName}>
      {(ids) => render(ids)}
    </Field>
  );
});
